'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { Logo } from './logo';
import type { LogoVariant } from './logo';
import { LogoSkeleton } from './logo-skeleton';

interface ISmartLogoProps {
  /**
   * Determines if the logo should include text
   * @default false
   */
  withText?: boolean;

  /**
   * Specifies the logo variant to use
   * @default 'auto'
   */
  variant?: LogoVariant;

  /**
   * Width of the logo in pixels
   * @default 180
   */
  width?: number;

  /**
   * Height of the logo in pixels
   * @default 38
   */
  height?: number;

  /**
   * Additional CSS classes to apply to the logo
   */
  className?: string;

  /**
   * Alt text for accessibility
   * @default 'Logo'
   */
  alt?: string;

  /**
   * Whether to prioritize loading of the logo
   * @default true
   */
  priority?: boolean;

  /**
   * Show skeleton while loading
   * @default true
   */
  showSkeleton?: boolean;

  /**
   * URL de logo personalizado (marca blanca). Si se proporciona, tiene prioridad sobre el logo estático.
   */
  customLogoUrl?: string | null;
}

/**
 * SmartLogo component that shows a skeleton while the image is loading
 * and handles all the variants of the logo
 */
export const SmartLogo: React.FC<ISmartLogoProps> = ({
  withText = false,
  variant = 'auto',
  width = 180,
  height = 38,
  className = '',
  alt = 'Logo',
  priority = true,
  showSkeleton = true,
  customLogoUrl,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  // Si hay logo de marca blanca, usar imagen remota
  if (customLogoUrl) {
    return (
      <div className="relative flex items-center justify-center" style={{ width, height }}>
        {isLoading && showSkeleton && <LogoSkeleton withText={withText} width={width} height={height} className={className} />}
        <div className={['relative', 'h-full', 'w-full', isLoading && showSkeleton ? 'opacity-0' : 'opacity-100'].join(' ')}>
          <Image
            src={customLogoUrl}
            alt={alt}
            fill
            sizes={`${width}px`}
            priority={priority}
            className={['object-contain', className].filter(Boolean).join(' ')}
            onLoad={handleLoadingComplete}
            unoptimized
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ width, height }}>
      {isLoading && showSkeleton && <LogoSkeleton withText={withText} width={width} height={height} className={className} />}

      <div className={isLoading && showSkeleton ? 'opacity-0' : 'opacity-100'}>
        <Logo
          withText={withText}
          variant={variant}
          width={width}
          height={height}
          className={className}
          alt={alt}
          priority={priority}
          preloadBothThemes={false}
          onLoad={handleLoadingComplete}
        />
      </div>
    </div>
  );
};
